import { getFullImageUrl, getThumbnailUrl } from "../utils/urlConfig";
import React, { useState, useEffect, useMemo } from 'react';
import { fetchFullTree } from '../services/treeService';
import { fetchAssetDetailsById, assignLocationToAsset, assignParentAsset } from '../services/assetService';
import { fetchLocationDetailsById, assignLocationToparent } from '../services/locationService';
import { toast } from 'react-toastify';
import styles from './TreeView.module.css';

import { FaMapMarkerAlt, FaFolder, FaBox, FaSearch, FaChevronRight, FaInfoCircle, FaTimes, FaExternalLinkAlt, FaBoxes } from 'react-icons/fa';

const TreeItem = ({ item, searchTerm, onShowDetails, onDropItem, depth = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(searchTerm.length > 0);
    const [isDragOver, setIsDragOver] = useState(false);

    useEffect(() => {
        if (searchTerm.length > 0) {
            setIsExpanded(true);
        }
    }, [searchTerm]);

    const hasChildren = item.children && item.children.length > 0;
    const isLocation = item.type === 'LOCATION';
    const isVirtual = item.type === 'VIRTUAL';

    const handleToggle = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    return (
        <li
            className={`${styles.treeItem} ${isDragOver ? styles.dropTargetActive : ''}`}
            style={{ marginLeft: depth > 0 ? '10px' : '0' }}
            onDragOver={(e) => {
                if (item.type !== 'VIRTUAL') {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragOver(true);
                }
            }}
            onDragLeave={(e) => {
                e.stopPropagation();
                setIsDragOver(false);
            }}
            onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(false);
                try {
                    const draggedData = JSON.parse(e.dataTransfer.getData('text/plain'));
                    onDropItem(draggedData, item);
                } catch (err) {
                    console.error("Drop error:", err);
                }
            }}
        >
            <div
                className={`${styles.itemContent} ${isLocation ? styles.locationRow : (isVirtual ? styles.virtualRow : styles.assetRow)} ${isExpanded && hasChildren ? styles.active : ''}`}
                onClick={hasChildren ? handleToggle : null}
                draggable={!isVirtual}
                onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', JSON.stringify({ id: item.id, type: item.type, name: item.name }));
                    // Use a timeout to apply dragging style after the drag image is created
                    setTimeout(() => e.target.closest(`.${styles.itemContent}`).classList.add(styles.dragging), 0);
                }}
                onDragEnd={(e) => {
                    e.target.closest(`.${styles.itemContent}`).classList.remove(styles.dragging);
                }}
            >
                <span
                    className={`${styles.toggleIcon} ${isExpanded ? styles.expanded : ''}`}
                >
                    {hasChildren && <FaChevronRight />}
                </span>

                <span className={`${styles.typeIcon} ${isLocation ? styles.locationIcon : (isVirtual ? styles.virtualIcon : styles.assetIcon)}`}>
                    {item.image ? (
                        <img
                            src={getThumbnailUrl(item.image)}
                            className={`${styles.treeThumbnail} ${isLocation ? styles.locationThumb : styles.assetThumb}`}
                            alt={item.name}
                        />
                    ) : (
                        isLocation ? <FaFolder /> : (isVirtual ? <FaBoxes style={{ color: 'var(--primary-color)' }} /> : <FaBox />)
                    )}
                </span>

                <span className={`${styles.itemName} ${isLocation ? styles.locationText : (isVirtual ? styles.virtualText : '')}`}>{item.name}</span>

                {!isVirtual && (
                    <span className={`${styles.typeTag} ${isLocation ? styles.locationTag : styles.assetTag}`}>
                        {isLocation ? 'Container' : 'Asset'}
                    </span>
                )}

                {!isLocation && !isVirtual && item.locationName && (
                    <span className={styles.locationBadge} title={`Located in: ${item.locationName}`}>
                        <FaMapMarkerAlt /> {item.locationName}
                    </span>
                )}

                {!isVirtual && item.qrCode && (
                    <span className={styles.qrBadge} title="QR Code">{item.qrCode}</span>
                )}

                {!isVirtual && (
                    <button
                        className={styles.detailsBtn}
                        onClick={(e) => { e.stopPropagation(); onShowDetails(item); }}
                        title="Show Details"
                    >
                        <FaInfoCircle />
                    </button>
                )}
            </div>

            {hasChildren && isExpanded && (
                <ul className={styles.childrenContainer}>
                    {item.children.map(child => (
                        <TreeItem
                            key={`${child.type}-${child.id}`}
                            item={child}
                            searchTerm={searchTerm}
                            onShowDetails={onShowDetails}
                            onDropItem={onDropItem}
                            depth={depth + 1}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
};

const TreeViewPage = () => {
    const [treeData, setTreeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [isRootDragOver, setIsRootDragOver] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const result = await fetchFullTree("unified");
        if (result.success) {
            setTreeData(result.data);
        }
        setLoading(false);
    };

    const isDescendant = (nodes, parentId, childId, childType) => {
        for (const node of nodes) {
            if (node.id === parentId) {
                // Check if childId is in this node's subtree
                const checkChildren = (children) => {
                    if (!children) return false;
                    for (const child of children) {
                        if (child.id === childId && child.type === childType) return true;
                        if (checkChildren(child.children)) return true;
                    }
                    return false;
                };
                return checkChildren(node.children);
            }
            if (node.children && isDescendant(node.children, parentId, childId, childType)) return true;
        }
        return false;
    };

    const handleRemap = async (draggedItem, targetItem) => {
        if (!draggedItem || !targetItem) return;
        if (draggedItem.id === targetItem.id && draggedItem.type === targetItem.type) return;

        // Block Container -> Asset
        if (draggedItem.type === 'LOCATION' && (targetItem.type === 'ASSET' || targetItem.type === 'ASSET_SPARE')) {
            toast.error("Containers cannot be placed inside Assets.");
            return;
        }

        // Check for circular dependency
        if (isDescendant(treeData, draggedItem.id, targetItem.id, targetItem.type)) {
            toast.error("Cannot move an item into its own descendant.");
            return;
        }

        if (!window.confirm(`Move "${draggedItem.name}" to ${targetItem.id === -1 ? 'Top Level' : `inside "${targetItem.name}"`}?`)) return;

        try {
            let res;
            if (draggedItem.type === 'ASSET' || draggedItem.type === 'ASSET_SPARE') {
                if (targetItem.id === -1) {
                    // Unassign location and parent
                    const locRes = await assignLocationToAsset(draggedItem.id, -1);
                    const parRes = await assignParentAsset(draggedItem.id, -1);
                    res = { success: (locRes.status === 200 || locRes.success) && (parRes.status === 200 || parRes.success) };
                } else if (targetItem.type === 'LOCATION') {
                    res = await assignLocationToAsset(draggedItem.id, targetItem.id);
                } else {
                    res = await assignParentAsset(draggedItem.id, targetItem.id);
                }
            } else if (draggedItem.type === 'LOCATION') {
                res = await assignLocationToparent(draggedItem.id, targetItem.id);
            }

            if (res && (res.status === 200 || res.success)) {
                toast.success("Inventory updated successfully!");
                loadData();
            } else {
                toast.error("Failed to update inventory.");
            }
        } catch (error) {
            console.error("Remapping error:", error);
            toast.error("An error occurred during remapping.");
        }
    };

    const handleShowDetails = async (item) => {
        setSelectedItem(item);
        setDetailLoading(true);
        setDetailData(null);
        try {
            const result = item.type === 'ASSET'
                ? await fetchAssetDetailsById(item.id)
                : await fetchLocationDetailsById(item.id);

            if (result.success || result.data) {
                setDetailData(result.data);
            }
        } catch (error) {
            console.error("Error fetching details:", error);
        } finally {
            setDetailLoading(false);
        }
    };

    const filteredTree = useMemo(() => {
        if (!searchTerm) return treeData;

        const filterNodes = (nodes) => {
            return nodes
                .map(node => {
                    const matches = node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (node.qrCode && node.qrCode.toLowerCase().includes(searchTerm.toLowerCase()));

                    const filteredChildren = node.children ? filterNodes(node.children) : [];

                    if (matches || filteredChildren.length > 0) {
                        return { ...node, children: filteredChildren };
                    }
                    return null;
                })
                .filter(node => node !== null);
        };

        return filterNodes(treeData);
    }, [treeData, searchTerm]);

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.emptyState}>
                    <i className={`fa-solid fa-circle-notch fa-spin ${styles.emptyIcon}`}></i>
                    <p>Building your inventory map...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Inventory Explorer</h1>
                <p className={styles.subtitle}>Unified view of containers and assets across your home</p>
            </div>

            <div className={styles.toolbar}>
                <div className={styles.searchWrapper}>
                    <FaSearch className={styles.searchIcon} />
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search assets or containers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className={`${styles.mainContent} ${selectedItem ? styles.hasDetail : ''}`}>
                <div className={styles.treeSection}>
                    <div className={styles.treeCard}>
                        <div
                            className={`${styles.rootDropZone} ${isRootDragOver ? styles.active : ''}`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsRootDragOver(true);
                            }}
                            onDragLeave={(e) => {
                                e.stopPropagation();
                                setIsRootDragOver(false);
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsRootDragOver(false);
                                try {
                                    const draggedData = JSON.parse(e.dataTransfer.getData('text/plain'));
                                    handleRemap(draggedData, { id: -1, type: 'ROOT', name: 'Top Level' });
                                } catch (err) {
                                    console.error("Drop error:", err);
                                }
                            }}
                        >
                            <FaBoxes className={styles.rootIcon} />
                            <span>Drop here to move to Top Level</span>
                        </div>
                        {filteredTree.length > 0 ? (
                            <ul className={styles.treeRoot}>
                                {filteredTree.map(item => (
                                    <TreeItem
                                        key={`${item.type}-${item.id}`}
                                        item={item}
                                        searchTerm={searchTerm}
                                        onShowDetails={handleShowDetails}
                                        onDropItem={handleRemap}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <div className={styles.emptyState}>
                                <FaFolder className={styles.emptyIcon} />
                                <p>{searchTerm ? "No matching items found." : "Your inventory is currently empty."}</p>
                            </div>
                        )}
                    </div>
                </div>

                {selectedItem && (
                    <div className={styles.detailOverlay} onClick={() => setSelectedItem(null)}>
                        <div className={styles.detailPanel} onClick={e => e.stopPropagation()}>
                            <div className={styles.detailHeader}>
                                <h2>{selectedItem.type === 'LOCATION' ? 'Container Details' : 'Asset Details'}</h2>
                                <button className={styles.closeBtn} onClick={() => setSelectedItem(null)}>
                                    <FaTimes />
                                </button>
                            </div>

                            <div className={styles.detailContent}>
                                {detailLoading ? (
                                    <div className={styles.detailLoading}>
                                        <i className="fa-solid fa-circle-notch fa-spin"></i>
                                        <p>Loading details...</p>
                                    </div>
                                ) : detailData ? (
                                    <>
                                        <div className={styles.detailVisual}>
                                            <div className={styles.detailImageFrame}>
                                                {detailData.image || (detailData.imageDetails && detailData.imageDetails.length > 0) ? (
                                                    <img
                                                        src={getThumbnailUrl(detailData.image || detailData.imageDetails.find(img => img.isPrimary)?.imagePath || detailData.imageDetails[0].imagePath)}
                                                        alt={detailData.name}
                                                        className={styles.detailImage}
                                                    />
                                                ) : (
                                                    <div className={styles.detailNoImage}>
                                                        {selectedItem.type === 'LOCATION' ? <FaFolder /> : <FaBox />}
                                                        <span>No image available</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className={styles.detailTypeBadge}>
                                                <span className={selectedItem.type === 'LOCATION' ? styles.locationTag : styles.assetTag}>
                                                    {selectedItem.type === 'LOCATION' ? 'Container' : 'Asset'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className={styles.detailInfo}>
                                            <h3 className={styles.detailName}>{detailData.name}</h3>
                                            <p className={styles.detailDesc}>{detailData.description || "No description provided."}</p>

                                            <div className={styles.detailGrid}>
                                                <div className={styles.detailGridItem}>
                                                    <strong>Internal ID</strong>
                                                    <span>{detailData.id || detailData.assetId}</span>
                                                </div>

                                                {/* Parent Container Mapping */}
                                                {selectedItem.type === 'ASSET' && detailData.location && (
                                                    <div className={styles.detailGridItem}>
                                                        <strong>Parent Container</strong>
                                                        <span className={styles.detailLinkText}>
                                                            <FaMapMarkerAlt /> {detailData.location.name}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedItem.type === 'LOCATION' && detailData.parentLocation && (
                                                    <div className={styles.detailGridItem}>
                                                        <strong>Parent Container</strong>
                                                        <span className={styles.detailLinkText}>
                                                            <FaMapMarkerAlt /> {detailData.parentLocation.name}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Parent Asset Mapping (Only for Assets) */}
                                                {selectedItem.type === 'ASSET' && detailData.parentAsset && (
                                                    <div className={styles.detailGridItem}>
                                                        <strong>Parent Asset</strong>
                                                        <span className={styles.detailLinkText}>
                                                            <FaBox /> {detailData.parentAsset.name}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Spare Of Mapping (Only for Assets) */}
                                                {selectedItem.type === 'ASSET' && detailData.spareOf && (
                                                    <div className={styles.detailGridItem}>
                                                        <strong>Spare Of</strong>
                                                        <span className={styles.detailLinkText}>
                                                            <FaBox /> {detailData.spareOf.name}
                                                        </span>
                                                    </div>
                                                )}

                                                {detailData.qrCode && (
                                                    <div className={styles.detailGridItem}>
                                                        <strong>QR Mapping</strong>
                                                        <div className={styles.detailQrFlex}>
                                                            <img
                                                                src={getFullImageUrl(detailData.qrCode)}
                                                                className={styles.detailQrImage}
                                                                alt="QR Code"
                                                            />
                                                            <span className={styles.detailQrText}>{detailData.qrCode}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Children / Contents Section */}
                                            <div className={styles.detailChildrenSection}>
                                                <h4>Contained Items</h4>
                                                <div className={styles.detailChildrenList}>
                                                    {/* Assets contained in this asset or location */}
                                                    {(detailData.childAssets || detailData.assets) &&
                                                        (detailData.childAssets || detailData.assets).length > 0 &&
                                                        (detailData.childAssets || detailData.assets).map(child => (
                                                            <div key={child.id} className={styles.detailChildItem}>
                                                                <FaBoxes className={styles.childIconAsset} />
                                                                <span>{child.name}</span>
                                                                <em className={styles.childType}>Asset</em>
                                                            </div>
                                                        ))
                                                    }

                                                    {/* Sub-locations contained in this location */}
                                                    {detailData.childLocations && detailData.childLocations.length > 0 &&
                                                        detailData.childLocations.map(child => (
                                                            <div key={child.id} className={styles.detailChildItem}>
                                                                <FaMapMarkerAlt className={styles.childIconLoc} />
                                                                <span>{child.name}</span>
                                                                <em className={styles.childType}>Container</em>
                                                            </div>
                                                        ))
                                                    }

                                                    {/* Empty State for children */}
                                                    {(!detailData.childAssets || detailData.childAssets.length === 0) &&
                                                        (!detailData.assets || detailData.assets.length === 0) &&
                                                        (!detailData.childLocations || detailData.childLocations.length === 0) && (
                                                            <p className={styles.detailEmptyText}>No items contained inside.</p>
                                                        )}
                                                </div>
                                            </div>

                                            <div className={styles.detailActions}>
                                                <a
                                                    href={selectedItem.type === 'LOCATION' ? `/public/locations/${selectedItem.id}` : `/public/assets/${selectedItem.id}`}
                                                    className={styles.viewFullBtn}
                                                >
                                                    View Full Page <FaExternalLinkAlt />
                                                </a>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className={styles.detailError}>
                                        <p>Failed to load details.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TreeViewPage;
